import Button from "common/components/Button/Button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SelectionActionBarProps {
  selectedCount: number;
  onDelete?: () => void;
  onClearSelection: () => void;
  isDeleting?: boolean;
}

function SelectionActionBar({
  selectedCount,
  onDelete,
  onClearSelection,
  isDeleting = false,
}: SelectionActionBarProps) {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(selectedCount > 0);

  useEffect(() => {
    if (selectedCount > 0) {
      setShouldRender(true);
      setIsExiting(false);
      return undefined;
    }
    if (shouldRender) {
      // Trigger exit animation
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [selectedCount, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 ${
        isExiting ? "animate-fade-out" : "animate-fade-in"
      }`}
    >
      <div className="bg-slate-800 text-white rounded-full shadow-2xl px-6 py-3 flex items-center gap-4">
        {/* Close button */}
        <button
          onClick={onClearSelection}
          className="hover:bg-slate-700 rounded-full p-1 transition-colors"
          aria-label="Clear selection"
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Selected count */}
        <div className="flex items-center gap-2 px-2">
          <span className="font-semibold text-sm">
            {t("common.itemsSelected", { count: selectedCount })}
          </span>
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-600" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          {onDelete && (
            <Button
              icon="TrashIcon"
              type="error"
              compact
              onClick={onDelete}
              loading={isDeleting}
              disabled={isDeleting}
              confirm
              confirmMessage={t("actions.confirmDeleteMultiple", {
                count: selectedCount,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SelectionActionBar;
